import React, { useState, useEffect } from 'react';
import API_BASE_URL from '../../config';
import './AdminCoupons.css';

const AdminCoupons = () => {
  const [coupons, setCoupons] = useState([]);
  const [productDiscounts, setProductDiscounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('coupons');
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [formData, setFormData] = useState({
    code: '',
    description: '',
    discount_type: 'percentage',
    discount_value: '',
    minimum_order_amount: '',
    maximum_discount_amount: '',
    start_date: '',
    end_date: '',
    usage_limit: '',
    is_active: 1
  });

  // Quantity Discount Form State
  const [qtyFormData, setQtyFormData] = useState({
    product_id: '',
    discount_type: 'percentage',
    discount_value: '',
    min_quantity: '',
    buy_quantity: '',
    get_quantity: '',
    start_date: '',
    end_date: '',
    is_active: 1
  });
  const [products, setProducts] = useState([]);
  const [showQtyForm, setShowQtyForm] = useState(false);
  const [editingQty, setEditingQty] = useState(null);

  useEffect(() => {
    fetchAllData();
  }, []);

  const fetchAllData = async () => {
    setLoading(true);
    setError(null);
    try {
      await Promise.all([
        fetchCoupons(),
        fetchProductDiscounts(),
        fetchProducts()
      ]);
    } catch (error) {
      console.error('Error fetching data:', error);
      setError('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const fetchCoupons = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/admin/coupons`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setCoupons(data.data || []);
        }
      }
    } catch (error) {
      console.error('Error fetching coupons:', error);
    }
  };

  const fetchProductDiscounts = async () => {
    try {
      console.log('Fetching product discounts...');
      const response = await fetch(`${API_BASE_URL}/admin/product-discounts`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      
      console.log('Response status:', response.status);
      
      if (response.ok) {
        const data = await response.json();
        console.log('Product discounts data:', data);
        if (data.success) {
          setProductDiscounts(data.data || []);
          console.log('Set product discounts count:', data.data?.length);
        } else {
          console.error('Error fetching product discounts:', data.message);
        }
      } else {
        const errorText = await response.text();
        console.error('Failed to fetch product discounts:', errorText);
      }
    } catch (error) {
      console.error('Error fetching product discounts:', error);
    }
  };

  const fetchProducts = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/admin/products`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setProducts(data.data || []);
        }
      }
    } catch (error) {
      console.error('Error fetching products:', error);
    }
  };

  // Coupon Handlers
  const handleCouponSubmit = async (e) => {
    e.preventDefault();
    if (!formData.code || !formData.discount_value) {
      alert('Coupon code and discount value are required');
      return;
    }

    try {
      const url = editingItem 
        ? `${API_BASE_URL}/admin/coupons/${editingItem.id}` 
        : `${API_BASE_URL}/admin/coupons`;
      const method = editingItem ? 'PUT' : 'POST';
      
      const response = await fetch(url, {
        method: method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(formData)
      });
      
      const data = await response.json();
      if (response.ok && data.success) {
        alert(editingItem ? 'Coupon updated successfully' : 'Coupon created successfully');
        fetchCoupons();
        resetCouponForm();
      } else {
        alert(data.message || 'Failed to save coupon');
      }
    } catch (error) {
      console.error('Error saving coupon:', error);
      alert('Error saving coupon');
    }
  };

  const handleDeleteCoupon = async (couponId, couponCode) => {
    if (!window.confirm(`Are you sure you want to delete coupon "${couponCode}"?`)) return;
    try {
      const response = await fetch(`${API_BASE_URL}/admin/coupons/${couponId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      const data = await response.json();
      if (response.ok && data.success) {
        alert('Coupon deleted successfully');
        fetchCoupons();
      } else {
        alert(data.message || 'Failed to delete coupon');
      }
    } catch (error) {
      console.error('Error deleting coupon:', error);
      alert('Error deleting coupon');
    }
  };

  // Quantity Discount Handlers
  const handleQtyDiscountSubmit = async (e) => {
    e.preventDefault();
    if (!qtyFormData.product_id) {
      alert('Please select a product');
      return;
    }
    
    if (qtyFormData.discount_type !== 'buy_x_get_y' && !qtyFormData.discount_value) {
      alert('Discount value is required');
      return;
    }

    try {
      const url = editingQty 
        ? `${API_BASE_URL}/admin/product-discounts/${editingQty.id}` 
        : `${API_BASE_URL}/admin/product-discounts`;
      const method = editingQty ? 'PUT' : 'POST';
      
      const response = await fetch(url, {
        method: method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(qtyFormData)
      });
      
      const data = await response.json();
      if (response.ok && data.success) {
        alert(editingQty ? 'Quantity discount updated successfully' : 'Quantity discount created successfully');
        fetchProductDiscounts();
        resetQtyForm();
      } else {
        alert(data.message || 'Failed to save quantity discount');
      }
    } catch (error) {
      console.error('Error saving quantity discount:', error);
      alert('Error saving quantity discount');
    }
  };

  const handleDeleteQtyDiscount = async (discountId, productName) => {
    if (!window.confirm(`Are you sure you want to delete discount for "${productName}"?`)) return;
    try {
      const response = await fetch(`${API_BASE_URL}/admin/product-discounts/${discountId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      const data = await response.json();
      if (response.ok && data.success) {
        alert('Quantity discount deleted successfully');
        fetchProductDiscounts();
      } else {
        alert(data.message || 'Failed to delete quantity discount');
      }
    } catch (error) {
      console.error('Error deleting quantity discount:', error);
      alert('Error deleting quantity discount');
    }
  };

  const resetCouponForm = () => {
    setFormData({
      code: '',
      description: '',
      discount_type: 'percentage',
      discount_value: '',
      minimum_order_amount: '',
      maximum_discount_amount: '',
      start_date: '',
      end_date: '',
      usage_limit: '',
      is_active: 1
    });
    setEditingItem(null);
    setShowAddForm(false);
  };

  const resetQtyForm = () => {
    setQtyFormData({
      product_id: '',
      discount_type: 'percentage',
      discount_value: '',
      min_quantity: '',
      buy_quantity: '',
      get_quantity: '',
      start_date: '',
      end_date: '',
      is_active: 1
    });
    setEditingQty(null);
    setShowQtyForm(false);
  };

  const editCoupon = (coupon) => {
    setFormData({
      code: coupon.code,
      description: coupon.description || '',
      discount_type: coupon.discount_type,
      discount_value: coupon.discount_value,
      minimum_order_amount: coupon.minimum_order_amount || '',
      maximum_discount_amount: coupon.maximum_discount_amount || '',
      start_date: coupon.start_date ? coupon.start_date.split(' ')[0] : '',
      end_date: coupon.end_date ? coupon.end_date.split(' ')[0] : '',
      usage_limit: coupon.usage_limit || '',
      is_active: coupon.is_active
    });
    setEditingItem(coupon);
    setShowAddForm(true);
  };

  const editQtyDiscount = (discount) => {
    setQtyFormData({
      product_id: discount.product_id,
      discount_type: discount.discount_type,
      discount_value: discount.discount_value || '',
      min_quantity: discount.min_quantity || '',
      buy_quantity: discount.buy_quantity || '',
      get_quantity: discount.get_quantity || '',
      start_date: discount.start_date ? discount.start_date.split(' ')[0] : '',
      end_date: discount.end_date ? discount.end_date.split(' ')[0] : '',
      is_active: discount.is_active
    });
    setEditingQty(discount);
    setShowQtyForm(true);
  };

  const formatPrice = (price) => {
    if (!price) return '₹0.00';
    return `₹${parseFloat(price).toFixed(2)}`;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).toLocaleDateString('en-IN');
    } catch (e) {
      return 'Invalid Date';
    }
  };

  const getProductName = (productId) => {
    const product = products.find(p => p.id === productId);
    return product ? product.name : 'Unknown Product';
  };

  if (loading) {
    return (
      <div className="admin-coupons-loading">
        <div className="spinner"></div>
        <p>Loading discounts...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="admin-coupons-error">
        <p>Error: {error}</p>
        <button onClick={() => window.location.reload()}>Retry</button>
      </div>
    );
  }

  return (
    <div className="admin-coupons">
      <div className="admin-coupons-header">
        <h1>Discounts Management</h1>
        <div className="tabs">
          <button className={`tab ${activeTab === 'coupons' ? 'active' : ''}`} onClick={() => setActiveTab('coupons')}>
            Coupons ({coupons.length})
          </button>
          <button className={`tab ${activeTab === 'quantity' ? 'active' : ''}`} onClick={() => setActiveTab('quantity')}>
            Quantity Discounts ({productDiscounts.length})
          </button>
        </div>
      </div>

      {/* Coupons Tab */}
      {activeTab === 'coupons' && (
        <>
          <div className="section-header">
            <h2>Coupons</h2>
            <button className="add-btn" onClick={() => setShowAddForm(true)}>+ Add New Coupon</button>
          </div>

          {showAddForm && (
            <div className="modal-overlay">
              <div className="modal-content">
                <div className="modal-header">
                  <h2>{editingItem ? 'Edit Coupon' : 'Add New Coupon'}</h2>
                  <button className="close-btn" onClick={resetCouponForm}>×</button>
                </div>
                <form onSubmit={handleCouponSubmit} className="coupon-form">
                  <div className="form-row">
                    <div className="form-group">
                      <label>Coupon Code *</label>
                      <input type="text" name="code" value={formData.code} onChange={(e) => setFormData({...formData, code: e.target.value.toUpperCase()})} required placeholder="WELCOME10" />
                    </div>
                    <div className="form-group">
                      <label>Discount Type</label>
                      <select name="discount_type" value={formData.discount_type} onChange={(e) => setFormData({...formData, discount_type: e.target.value})}>
                        <option value="percentage">Percentage (%)</option>
                        <option value="fixed">Fixed Amount (₹)</option>
                      </select>
                    </div>
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label>Discount Value *</label>
                      <input type="number" name="discount_value" value={formData.discount_value} onChange={(e) => setFormData({...formData, discount_value: e.target.value})} required step="0.01" />
                    </div>
                    <div className="form-group">
                      <label>Minimum Order Amount</label>
                      <input type="number" name="minimum_order_amount" value={formData.minimum_order_amount} onChange={(e) => setFormData({...formData, minimum_order_amount: e.target.value})} step="0.01" />
                    </div>
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label>Max Discount Amount</label>
                      <input type="number" name="maximum_discount_amount" value={formData.maximum_discount_amount} onChange={(e) => setFormData({...formData, maximum_discount_amount: e.target.value})} step="0.01" />
                    </div>
                    <div className="form-group">
                      <label>Usage Limit</label>
                      <input type="number" name="usage_limit" value={formData.usage_limit} onChange={(e) => setFormData({...formData, usage_limit: e.target.value})} />
                    </div>
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label>Start Date</label>
                      <input type="date" name="start_date" value={formData.start_date} onChange={(e) => setFormData({...formData, start_date: e.target.value})} />
                    </div>
                    <div className="form-group">
                      <label>End Date</label>
                      <input type="date" name="end_date" value={formData.end_date} onChange={(e) => setFormData({...formData, end_date: e.target.value})} />
                    </div>
                  </div>

                  <div className="form-group">
                    <label>Description</label>
                    <textarea name="description" value={formData.description} onChange={(e) => setFormData({...formData, description: e.target.value})} rows="2" />
                  </div>

                  <div className="form-group checkbox">
                    <label><input type="checkbox" checked={formData.is_active === 1} onChange={(e) => setFormData({...formData, is_active: e.target.checked ? 1 : 0})} /> Active</label>
                  </div>

                  <div className="form-actions">
                    <button type="submit" className="submit-btn">{editingItem ? 'Update Coupon' : 'Create Coupon'}</button>
                    <button type="button" className="cancel-btn" onClick={resetCouponForm}>Cancel</button>
                  </div>
                </form>
              </div>
            </div>
          )}

          <div className="coupons-table-container">
            {coupons.length === 0 ? (
              <div className="no-data">No coupons found. Click "Add New Coupon" to create one.</div>
            ) : (
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Code</th>
                    <th>Discount</th>
                    <th>Min Order</th>
                    <th>Max Discount</th>
                    <th>Usage</th>
                    <th>Valid Period</th>
                    <th>Status</th>
                    <th>Actions</th>
                   </tr>
                </thead>
                <tbody>
                  {coupons.map(coupon => (
                    <tr key={coupon.id}>
                      <td className="coupon-code">{coupon.code}</td>
                      <td className="discount-value">{coupon.discount_type === 'percentage' ? `${coupon.discount_value}%` : `₹${coupon.discount_value}`}</td>
                      <td>{coupon.minimum_order_amount ? `₹${coupon.minimum_order_amount}` : 'No min'}</td>
                      <td>{coupon.maximum_discount_amount ? `₹${coupon.maximum_discount_amount}` : 'No limit'}</td>
                      <td>{coupon.usage_count || 0}/{coupon.usage_limit || '∞'}</td>
                      <td className="date-range">{formatDate(coupon.start_date)} - {formatDate(coupon.end_date)}</td>
                      <td><span className={`status-badge ${coupon.is_active === 1 ? 'active' : 'inactive'}`}>{coupon.is_active === 1 ? 'Active' : 'Inactive'}</span></td>
                      <td className="actions-cell">
                        <button className="edit-btn" onClick={() => editCoupon(coupon)} title="Edit">✏️</button>
                        <button className="delete-btn" onClick={() => handleDeleteCoupon(coupon.id, coupon.code)} title="Delete">🗑️</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </>
      )}

      {/* Quantity Discounts Tab */}
      {activeTab === 'quantity' && (
        <>
          <div className="section-header">
            <h2>Quantity Discounts</h2>
            <button className="add-btn" onClick={() => setShowQtyForm(true)}>+ Add New Discount</button>
            <button className="refresh-btn" onClick={fetchProductDiscounts}>⟳ Refresh</button>
          </div>

          {showQtyForm && (
            <div className="modal-overlay">
              <div className="modal-content">
                <div className="modal-header">
                  <h2>{editingQty ? 'Edit Quantity Discount' : 'Add New Quantity Discount'}</h2>
                  <button className="close-btn" onClick={resetQtyForm}>×</button>
                </div>
                <form onSubmit={handleQtyDiscountSubmit} className="coupon-form">
                  <div className="form-group">
                    <label>Product *</label>
                    <select value={qtyFormData.product_id} onChange={(e) => setQtyFormData({...qtyFormData, product_id: e.target.value})} required>
                      <option value="">Select Product</option>
                      {products.map(product => (
                        <option key={product.id} value={product.id}>{product.name} - {product.brand}</option>
                      ))}
                    </select>
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label>Discount Type</label>
                      <select value={qtyFormData.discount_type} onChange={(e) => {
                        setQtyFormData({...qtyFormData, discount_type: e.target.value});
                        if (e.target.value !== 'buy_x_get_y') {
                          setQtyFormData(prev => ({...prev, buy_quantity: '', get_quantity: ''}));
                        }
                      }}>
                        <option value="percentage">Percentage (%)</option>
                        <option value="fixed">Fixed Amount (₹)</option>
                        <option value="buy_x_get_y">Buy X Get Y Free</option>
                      </select>
                    </div>
                    <div className="form-group">
                      <label>Discount Value *</label>
                      <input type="number" value={qtyFormData.discount_value} onChange={(e) => setQtyFormData({...qtyFormData, discount_value: e.target.value})} required step="0.01" />
                    </div>
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label>Minimum Quantity</label>
                      <input type="number" value={qtyFormData.min_quantity} onChange={(e) => setQtyFormData({...qtyFormData, min_quantity: e.target.value})} placeholder="1" />
                    </div>
                    {qtyFormData.discount_type === 'buy_x_get_y' && (
                      <>
                        <div className="form-group">
                          <label>Buy Quantity</label>
                          <input type="number" value={qtyFormData.buy_quantity} onChange={(e) => setQtyFormData({...qtyFormData, buy_quantity: e.target.value})} placeholder="2" />
                        </div>
                        <div className="form-group">
                          <label>Get Free Quantity</label>
                          <input type="number" value={qtyFormData.get_quantity} onChange={(e) => setQtyFormData({...qtyFormData, get_quantity: e.target.value})} placeholder="1" />
                        </div>
                      </>
                    )}
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label>Start Date</label>
                      <input type="date" value={qtyFormData.start_date} onChange={(e) => setQtyFormData({...qtyFormData, start_date: e.target.value})} />
                    </div>
                    <div className="form-group">
                      <label>End Date</label>
                      <input type="date" value={qtyFormData.end_date} onChange={(e) => setQtyFormData({...qtyFormData, end_date: e.target.value})} />
                    </div>
                  </div>

                  <div className="form-group checkbox">
                    <label><input type="checkbox" checked={qtyFormData.is_active === 1} onChange={(e) => setQtyFormData({...qtyFormData, is_active: e.target.checked ? 1 : 0})} /> Active</label>
                  </div>

                  <div className="form-actions">
                    <button type="submit" className="submit-btn">{editingQty ? 'Update Discount' : 'Create Discount'}</button>
                    <button type="button" className="cancel-btn" onClick={resetQtyForm}>Cancel</button>
                  </div>
                </form>
              </div>
            </div>
          )}

          <div className="coupons-table-container">
            {productDiscounts.length === 0 ? (
              <div className="no-data">No quantity discounts found. Click "Add New Discount" to create one.</div>
            ) : (
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Product</th>
                    <th>Discount Type</th>
                    <th>Discount Value</th>
                    <th>Min Quantity</th>
                    <th>Buy X Get Y</th>
                    <th>Valid Period</th>
                    <th>Status</th>
                    <th>Actions</th>
                   </tr>
                </thead>
                <tbody>
                  {productDiscounts.map(discount => (
                    <tr key={discount.id}>
                      <td className="product-name">{getProductName(discount.product_id)}</td>
                      <td><span className="discount-type-badge">{discount.discount_type === 'percentage' ? 'Percentage' : discount.discount_type === 'fixed' ? 'Fixed' : 'Buy X Get Y'}</span></td>
                      <td className="discount-value">{discount.discount_type === 'percentage' ? `${discount.discount_value}%` : discount.discount_type === 'fixed' ? `₹${discount.discount_value}` : 'Buy X Get Y'}</td>
                      <td>{discount.min_quantity || 1}+</td>
                      <td>{discount.discount_type === 'buy_x_get_y' ? `Buy ${discount.buy_quantity} Get ${discount.get_quantity} Free` : '-'}</td>
                      <td className="date-range">{formatDate(discount.start_date)} - {formatDate(discount.end_date)}</td>
                      <td><span className={`status-badge ${discount.is_active === 1 ? 'active' : 'inactive'}`}>{discount.is_active === 1 ? 'Active' : 'Inactive'}</span></td>
                      <td className="actions-cell">
                        <button className="edit-btn" onClick={() => editQtyDiscount(discount)} title="Edit">✏️</button>
                        <button className="delete-btn" onClick={() => handleDeleteQtyDiscount(discount.id, getProductName(discount.product_id))} title="Delete">🗑️</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default AdminCoupons;